import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import RocketCta from "@/components/RocketCta";
import {
  ArrowRight, Sparkles, AlertTriangle, TrendingUp, Brain, Zap, ShieldCheck,
  BarChart3, Webhook, Code2, Check, ChevronDown, Lock, FileCheck,
} from "lucide-react";
import RoiCalculator from "@/components/RoiCalculator";
import DiagnosticCTA, { diagnosticLink } from "@/components/DiagnosticCTA";
import PublicHeader from "@/components/PublicHeader";

const Feature = ({ icon: Icon, title, desc }) => (
  <article data-testid={`feature-${title}`} className="group p-7 rounded-xl border border-border bg-card hover:border-primary/40 hover:-translate-y-1 transition-all duration-300">
    <div className="w-11 h-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-5">
      <Icon className="w-5 h-5 text-primary" />
    </div>
    <h3 className="font-display text-lg font-semibold mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
  </article>
);

const PLATFORMS = [
  { name: "Bling ERP", color: "bg-blue-500/15 text-blue-400 border-blue-500/30", tag: "RECOMENDADO" },
  { name: "Shopify", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  { name: "Mercado Livre", color: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  { name: "VTEX", color: "bg-pink-500/15 text-pink-400 border-pink-500/30" },
  { name: "WooCommerce", color: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  { name: "API REST", color: "bg-primary/15 text-primary border-primary/30", tag: "QUALQUER SISTEMA" },
];

const PRICING = [
  {
    name: "Starter",
    price: "R$ 149",
    period: "por mês",
    desc: "Organize seu estoque",
    features: [
      "Dashboard inteligente + score de saúde",
      "Alertas básicos (ruptura, excesso, parados)",
      "Indicadores essenciais (giro, cobertura, sell-through)",
      "Relatórios básicos (mais vendidos, lentos)",
      "IA básica de insights",
      "Importação CSV / planilhas / ERP",
      "Suporte por e-mail",
    ],
    cta: "Começar trial 14 dias",
    planId: "starter",
    highlight: false,
  },
  {
    name: "Growth",
    price: "R$ 397",
    period: "por mês",
    desc: "Preveja demanda com IA",
    features: [
      "Tudo do Starter +",
      "IA de previsão de demanda + sazonalidade",
      "Recomendações inteligentes de compra",
      "Copiloto IA conversacional",
      "Alertas avançados + dashboard premium",
      "Simulador de cenários (preço × marketing)",
      "Integração CSV / planilhas / API REST",
      "Conectores Shopify, Bling, Tiny, Omie, Nuvemshop (em breve)",
      "Suporte prioritário (WhatsApp + onboarding)",
    ],
    cta: "Começar trial 14 dias",
    planId: "growth",
    highlight: true,
  },
  {
    name: "Pro",
    price: "R$ 1.497",
    period: "por mês",
    desc: "Inteligência completa para redes",
    features: [
      "Tudo do Growth +",
      "Multi-loja inteligente (comparação + redistribuição)",
      "Benchmarking de mercado por setor",
      "IA preditiva avançada (regional + sazonalidade profunda)",
      "Gestão de capital + previsão financeira avançada",
      "Radar de tendências + IA Anti-Prejuízo",
      "Relatórios executivos (DRE operacional)",
      "API Premium + integrações personalizadas (em breve)",
      "Suporte VIP + especialista dedicado",
    ],
    cta: "Começar trial 14 dias",
    planId: "pro",
    highlight: false,
  },
];

const FAQ = [
  { q: "Como a SmartStockAI prevê demanda?", a: "Modelos de IA analisam seu histórico de vendas, sazonalidade semanal/anual e tendências para projetar a demanda dos próximos 30 dias por produto. Quanto mais dados, mais precisa a previsão." },
  { q: "Preciso integrar com meu PDV ou ERP?", a: "Não é obrigatório. Você pode importar via CSV, cadastrar produtos manualmente, ou usar nossa API REST com guias prontos para Bling, Shopify, Mercado Livre, VTEX e WooCommerce." },
  { q: "É seguro? Meus dados são compartilhados?", a: "Sim, totalmente seguro. Conformidade com LGPD, criptografia TLS em todas as comunicações e armazenamento isolado por cliente. Benchmarks de mercado usam dados anonimizados e agregados — nenhuma informação individual é exposta." },
  { q: "Funciona para qualquer setor de varejo?", a: "Sim. Temos benchmarks específicos para 11 setores: supermercados, moda, farmácia, pet shop, eletrônicos, casa & decoração, beleza, bebidas, padaria, construção e varejo geral." },
  { q: "Em quanto tempo vejo resultado?", a: "Os primeiros insights aparecem em segundos após o cadastro. Reduções concretas de ruptura e estoque parado costumam aparecer em 14 a 30 dias com uso ativo." },
  { q: "Posso cancelar a qualquer momento?", a: "Sim. Sem fidelidade, sem multa. Você cancela direto no app ou pelo suporte e mantém acesso até o fim do ciclo pago." },
];

const NUMBERS = [
  { v: "94%", l: "precisão média de previsão" },
  { v: "60%", l: "redução de ruptura observada" },
  { v: "30s", l: "tempo médio de setup" },
  { v: "11", l: "setores com benchmark próprio" },
];

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <PublicHeader />

      {/* HERO */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-20">
        <div className="max-w-3xl fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-xs text-primary mb-6">
            <Sparkles className="w-3.5 h-3.5" /> #1 em previsão de demanda para varejo brasileiro
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
            Pare de perder venda<br />por <span className="text-primary">ruptura de estoque.</span>
          </h1>
          <p className="text-lg text-muted-foreground mt-7 max-w-2xl leading-relaxed">
            SmartStockAI usa <strong className="text-foreground">inteligência artificial</strong> para prever demanda, identificar
            estoque parado e recomendar compras automaticamente. Reduza ruptura em até 60% e libere capital parado em dias, não meses.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-10">
            <RocketCta
              testId="hero-cta-btn"
              to="/signup"
              label="Testar Grátis"
              subLabel="14 dias · sem cartão"
              size="lg"
            />
            <Link to="/#pricing">
              <Button variant="outline" size="lg" className="h-14 px-7 rounded-full border-border">
                Ver planos
              </Button>
            </Link>
            <a
              href={diagnosticLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-diagnostic-btn"
              className="inline-flex items-center h-14 px-6 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-medium text-sm"
            >
              Fazer Diagnóstico Gratuito
            </a>
          </div>
          <div className="flex items-center gap-6 mt-10 text-xs text-muted-foreground flex-wrap">
            <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary" /> Sem cartão de crédito</div>
            <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary" /> Setup em 30 segundos</div>
            <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary" /> Cancele quando quiser</div>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 fade-up" style={{ animationDelay: "150ms" }}>
          <div className="p-6 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider"><AlertTriangle className="w-3.5 h-3.5 text-primary" /> O que vai faltar</div>
            <div className="font-display text-4xl font-bold mt-3">7 produtos</div>
            <div className="text-sm text-muted-foreground mt-1">em risco de ruptura nos próximos 7 dias</div>
          </div>
          <div className="p-6 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider"><TrendingUp className="w-3.5 h-3.5 text-primary" /> O que está parado</div>
            <div className="font-display text-4xl font-bold mt-3">R$ 24.380</div>
            <div className="text-sm text-muted-foreground mt-1">imobilizados em produtos sem giro</div>
          </div>
          <div className="p-6 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider"><Brain className="w-3.5 h-3.5 text-primary" /> Recomendação IA</div>
            <div className="font-display text-4xl font-bold mt-3">R$ 18.920</div>
            <div className="text-sm text-muted-foreground mt-1">de compra sugerida pela IA</div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF — STATS */}
      <section className="relative z-10 border-y border-border/50 bg-card/30 py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {NUMBERS.map((n) => (
            <div key={n.l} className="text-center">
              <div className="font-display text-4xl sm:text-5xl font-bold text-primary">{n.v}</div>
              <div className="text-xs text-muted-foreground mt-2 uppercase tracking-wider">{n.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Plataforma completa</p>
          <h2 className="font-display text-4xl font-bold tracking-tight">Toda a inteligência do seu varejo em um só lugar.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Feature icon={TrendingUp} title="Previsão de demanda com IA" desc="Modelos preditivos antecipam quanto você vai vender de cada produto nos próximos 30 dias." />
          <Feature icon={AlertTriangle} title="Previsão de ruptura" desc="Saiba exatamente em quantos dias um item vai esgotar e o que comprar para evitar." />
          <Feature icon={ShieldCheck} title="Score de saúde do estoque" desc="Uma única nota que resume a saúde do seu estoque. Compare com lojas do seu setor." />
          <Feature icon={Brain} title="Copiloto IA (Claude, GPT, Gemini)" desc="Pergunte em português: 'O que devo promover essa semana?' — receba ações claras." />
          <Feature icon={Zap} title="Alertas inteligentes priorizados" desc="Notificações critical, high e medium. Foco no que importa para o resultado." />
          <Feature icon={BarChart3} title="Simulador de impacto financeiro" desc="Teste cenários de preço e marketing antes de aplicar — veja o ROI projetado." />
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section id="roi" className="relative z-10 max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <RoiCalculator />
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative z-10 max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Em 30 segundos</p>
            <h2 className="font-display text-4xl font-bold mb-6 tracking-tight">Entenda o que vai faltar, o que está parado e o que comprar.</h2>
            <p className="text-muted-foreground leading-relaxed">
              SmartStockAI consolida vendas, estoque e sazonalidade em dashboards executivos. Sem ERPs travados. Sem planilhas infinitas. Apenas decisão.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { n: "01", t: "Conecte ou importe", d: "Cadastre produtos manualmente, importe um CSV ou comece com nossos dados demo realistas." },
              { n: "02", t: "A IA aprende seu negócio", d: "Modelos analisam histórico de vendas, sazonalidade e padrões semanais." },
              { n: "03", t: "Decida com confiança", d: "Receba recomendações automáticas de compra e alertas priorizados de ruptura." },
            ].map((s) => (
              <div key={s.n} className="p-5 rounded-xl border border-border bg-card flex gap-4">
                <div className="font-mono text-primary text-sm shrink-0 mt-0.5">{s.n}</div>
                <div>
                  <div className="font-display font-semibold">{s.t}</div>
                  <div className="text-sm text-muted-foreground mt-1">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section id="integrations" className="relative z-10 max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3 flex items-center gap-2">
              <Webhook className="w-3.5 h-3.5" /> Automação completa
            </p>
            <h2 className="font-display text-4xl font-bold tracking-tight">
              Conecta com seu PDV, e-commerce e ERP em <span className="text-primary">2 linhas de código</span>.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              Cada venda atualiza o estoque automaticamente. Sem planilhas, sem importação manual, sem atraso.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
              {PLATFORMS.map((p) => (
                <div key={p.name} className="p-4 rounded-xl border border-border bg-card hover:border-primary/40 transition-all">
                  <span className={`text-xs px-2 py-0.5 rounded-md border font-mono ${p.color}`}>{p.name}</span>
                  {p.tag && <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{p.tag}</div>}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/30">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                </div>
                <span className="text-xs text-muted-foreground font-mono ml-2">registrar-venda.sh</span>
              </div>
              <Code2 className="w-4 h-4 text-muted-foreground" />
            </div>
            <pre className="p-5 text-[13px] font-mono leading-relaxed overflow-x-auto whitespace-pre">{`# A cada venda no seu PDV/e-commerce:
curl -X POST https://smartstockai.com/api/integrations/sale \\
  -H "X-API-Key: dai_••••••••••••" \\
  -H "Content-Type: application/json" \\
  -d '{"sku":"COCA-2L","qty":2,"external_id":"pedido_4521"}'

# Resposta instantânea:
{
  "status": "ok",
  "product": { "sku": "COCA-2L", "stock": 478 },
  "revenue": 19.80
}`}</pre>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative z-10 max-w-7xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Preços simples</p>
          <h2 className="font-display text-4xl font-bold tracking-tight">Comece grátis. Pague apenas quando crescer.</h2>
          <p className="text-muted-foreground mt-4">Sem fidelidade. Sem multa de cancelamento. Cancele quando quiser.</p>
          <div className="inline-flex items-center gap-2 mt-5 px-3.5 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-xs text-foreground/80">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span>Disponível hoje: importação CSV / planilha + API REST. Conectores nativos chegam em breve.</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRICING.map((p) => (
            <article key={p.name} data-testid={`pricing-${p.name}`} className={`p-7 rounded-xl border ${p.highlight ? "border-primary glow-coral relative" : "border-border"} bg-card flex flex-col`}>
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] px-3 py-1 rounded-full bg-primary text-white font-medium uppercase tracking-widest">
                  Mais popular
                </div>
              )}
              <div className="font-display text-xl font-semibold">{p.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{p.desc}</div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className={`font-display text-5xl font-bold ${p.highlight ? "text-primary" : ""}`}>{p.price}</span>
                <span className="text-sm text-muted-foreground">{p.period}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => {
                  const soon = f.includes("(em breve)");
                  const label = soon ? f.replace(/\s*\(em breve\)\s*/i, "") : f;
                  return (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${soon ? "text-muted-foreground/60" : "text-primary"}`} />
                      <span className={soon ? "text-muted-foreground/80" : ""}>
                        {label}
                        {soon && (
                          <span className="ml-1.5 inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-primary/30 bg-primary/5 text-primary align-middle">
                            Em breve
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-7">
                {p.highlight ? (
                  <RocketCta
                    to={`/signup?plan=${p.planId}`}
                    label="Testar Grátis"
                    subLabel="14 dias · sem cartão"
                    size="md"
                    testId={`pricing-cta-${p.planId}`}
                    className="w-full"
                  />
                ) : (
                  <Link to={`/signup?plan=${p.planId}`} className="block">
                    <Button
                      data-testid={`pricing-cta-${p.planId}`}
                      variant="outline"
                      className="w-full h-11 rounded-full border-primary/40 text-primary hover:bg-primary/10"
                    >
                      <span className="text-base mr-2">🚀</span> Testar Grátis
                    </Button>
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <div className="rounded-2xl border border-border bg-card p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Lock, t: "Criptografia TLS", d: "Dados protegidos em trânsito e em repouso" },
              { icon: FileCheck, t: "LGPD Compliant", d: "Conforme com a legislação brasileira" },
              { icon: ShieldCheck, t: "99.9% Uptime", d: "Infraestrutura cloud-native escalável" },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="font-display font-semibold">{t}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-3xl mx-auto px-6 py-20 border-t border-border/50">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Dúvidas frequentes</p>
          <h2 className="font-display text-4xl font-bold tracking-tight">Perguntas que todo lojista faz.</h2>
        </div>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <article key={f.q} data-testid={`faq-${i}`} className="rounded-xl border border-border bg-card overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full px-5 py-4 flex items-center justify-between text-left gap-3">
                <span className="font-medium">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* LEAD MAGNET */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <DiagnosticCTA />
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24 border-t border-border/50">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-12 text-center">
          <h2 className="font-display text-4xl font-bold tracking-tight">Pronto para nunca mais perder uma venda?</h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">Comece gratuitamente em 30 segundos. Sem cartão, sem compromisso.</p>
          <div className="mt-8 flex justify-center">
            <RocketCta
              testId="cta-bottom-btn"
              to="/signup"
              label="Testar Grátis"
              subLabel="14 dias · sem cartão"
              size="lg"
            />
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border/50 py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-white" /></div>
              <span className="font-display font-bold">SmartStockAI</span>
            </div>
            <p className="text-xs text-muted-foreground">Inteligência preditiva de estoque para o varejo brasileiro.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Produto</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/#features" className="hover:text-foreground">Funcionalidades</Link></li>
              <li><Link to="/#pricing" className="hover:text-foreground">Preços</Link></li>
              <li><Link to="/#integrations" className="hover:text-foreground">Integrações</Link></li>
              <li><Link to="/#roi" className="hover:text-foreground">Calculadora ROI</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Empresa</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/sobre" className="hover:text-foreground">Sobre nós</Link></li>
              <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
              <li><Link to="/#faq" className="hover:text-foreground">FAQ</Link></li>
              <li><a href="mailto:contato@smartstockai.com" className="hover:text-foreground">Contato</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Legal</div>
            <ul className="space-y-2 text-muted-foreground">
              <li>LGPD</li>
              <li>Termos de uso</li>
              <li>Privacidade</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-border/50 text-center text-xs text-muted-foreground">
          © 2026 SmartStockAI · Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
}
